// Single shared WebSocket connection for the whole app: one socket per browser
// tab instead of every component opening its own. Used for two things over
// the same connection:
//   1. Broadcasts — one-way "something changed" pushes from the server
//      (existing messages, shaped {type: "..."})
//   2. RPC requests — wsRequest(action, payload, token) sends {id, action,
//      payload, token} and resolves/rejects when the server replies with
//      {id, ok, data} or {id, ok:false, error, status}
//
// This is being introduced gradually — most of the app still uses REST
// fetch() calls, migrated action-by-action rather than all at once.

let ws = null;
let reconnectTimer = null;
let heartbeatInterval = null;
let heartbeatTimeout = null;
let nextId = 1;
const pending = new Map(); // id -> {resolve, reject}
const broadcastListeners = new Set();
// Separate from broadcastListeners above (those are server-initiated
// push messages; this fires from the request/response path itself) —
// subscribed to once, centrally, by AdminApp rather than checking
// err.status === 428 after every individual wsRequest call site
// throughout the admin panel. The calling code's own promise still
// rejects normally either way — this is purely an additional, global
// notification for "the current account needs to set up 2FA before
// continuing", not a replacement for handling the rejection itself.
const mandatoryTotpListeners = new Set();
// Same pattern as mandatoryTotpListeners above, for a different signal:
// an existing, previously-valid session token that the backend no
// longer recognizes (expired, or the account/data it pointed at is
// gone — e.g. a stale tab left open across a fresh install wiping
// every account). Deliberately does NOT fire on every 401 — auth.login
// itself legitimately returns 401 for "incorrect email or password",
// which is a wrong-credentials failure, not an invalidated session, and
// must never trigger a global "clear the session and show login" — see
// the hadToken tracking below, which is what tells these two apart.
const sessionInvalidListeners = new Set();
// Ambient context for the current page load, not per-request state like
// token is — set once by App.jsx on mount when it's rendered at
// /demo-route/<token>, then automatically included on every wsRequest
// call from here on. Deliberately not threaded through every individual
// call site the way token is, since that would mean updating dozens of
// unrelated call sites throughout App.jsx just to carry one value that's
// really a property of which page is currently loaded, not of any one
// request.
let demoRouteToken = null;
export function setDemoRouteToken(t) {
  demoRouteToken = t || null;
}
export function getDemoRouteToken() {
  return demoRouteToken;
}

const HEARTBEAT_INTERVAL_MS = 20000;
const HEARTBEAT_TIMEOUT_MS = 8000;

function stopHeartbeat() {
  if (heartbeatInterval) clearInterval(heartbeatInterval);
  if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
  heartbeatInterval = null;
  heartbeatTimeout = null;
}

// A WebSocket's readyState can report OPEN even though the underlying
// connection has actually died — e.g. after a laptop wakes from sleep, a
// network change, or the server restarting without a clean close ever
// reaching the client. wsRequest's "is it open" check alone can't catch
// this, so a request sent on a zombie connection would just be lost —
// nothing throws, nothing ever responds, it hangs until the browser's own
// TCP stack eventually notices (which can take a long time, or never,
// depending on the platform). This proactively pings every interval and
// force-closes the socket if a pong doesn't come back in time, so the
// existing reconnect logic kicks in immediately instead of leaving a
// zombie connection sitting there looking fine.
function startHeartbeat() {
  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "ping" }));
    } catch {
      ws.close();
      return;
    }
    heartbeatTimeout = setTimeout(() => {
      if (ws) ws.close(); // triggers onclose -> reconnect + pending-request rejection below
    }, HEARTBEAT_TIMEOUT_MS);
  }, HEARTBEAT_INTERVAL_MS);
}

// Every broadcast type worth resyncing on a connection gap — most are
// global (source of truth: grep `broadcast({ type: "..."` in server.js).
// profile-updated is targeted (via notifyUser, not broadcast) but still
// safe to include: it only triggers a harmless refetch of this account's
// own data. session-superseded is deliberately excluded — that's a
// specific security event tied to an actual newer login, not a "data may
// have changed" signal; firing it here would incorrectly sign someone out
// just because their own connection happened to reconnect.
const ALL_BROADCAST_TYPES = [
  "activity-log-changed",
  "api-settings-changed",
  "competitions-changed",
  "legal-docs-changed",
  "profile-updated",
  "purchase",
  "reminders-changed",
  "site-notifications-changed",
  "site-settings-changed",
  "users-changed",
];

function resyncEverything() {
  ALL_BROADCAST_TYPES.forEach((type) => {
    broadcastListeners.forEach((fn) => fn({ type }));
  });
}

// A backgrounded tab is a very plausible way to hit the gap resyncEverything
// fixes in the first place — an admin waiting for a competition to end is
// exactly the kind of thing someone tabs away from, and browsers throttle
// timers in background tabs, which can leave a connection stale (or trip
// the heartbeat into reconnecting, see ws.onopen below) without the tab
// itself ever registering as having "disconnected". Catches that case even
// when the WS connection technically never fully dropped through the
// throttling.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") resyncEverything();
});

function connect() {
  ws = new WebSocket(`${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws`);

  ws.onopen = () => {
    startHeartbeat();
    // The real fix for data silently going stale after a dropped
    // connection: every component that cares about live updates only
    // ever reacts to a broadcast it's actively connected to receive —
    // there was nothing that re-synced state once a connection came
    // back, so a real change whose broadcast was missed during any gap
    // (network blip, backend restart, laptop sleep, mobile network
    // handoff) just stayed missed indefinitely, with nothing to notice
    // or correct for it short of a manual page reload. Synthesizing
    // every broadcast type here means every existing listener refreshes
    // itself exactly as it already knows how to on a real change — no
    // component needs to know or care that this one didn't originate
    // from the server.
    resyncEverything();
  };

  ws.onmessage = (event) => {
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }
    if (msg && msg.type === "pong") {
      if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
      return;
    }
    if (msg && msg.id !== undefined && pending.has(msg.id)) {
      const { resolve, reject, hadToken } = pending.get(msg.id);
      pending.delete(msg.id);
      if (msg.ok) {
        resolve(msg.data);
      } else {
        if (msg.status === 428) mandatoryTotpListeners.forEach((fn) => fn());
        // Only a request that actually carried a token can mean "this
        // session is no longer valid" — a token-less 401 (auth.login's
        // own wrong-password case) is a completely different, expected
        // failure that must never trigger a global sign-out.
        if (msg.status === 401 && hadToken) sessionInvalidListeners.forEach((fn) => fn());
        // canDefer: the server will wait and apply the change itself if
        // asked — see "Use API exclusively" in the admin panel.
        reject(
          Object.assign(new Error(msg.error || "Request failed"), {
            status: msg.status,
            ...(msg.canDefer ? { canDefer: true } : {}),
          })
        );
      }
      return;
    }
    if (msg && msg.type) {
      broadcastListeners.forEach((fn) => fn(msg));
    }
  };

  ws.onclose = () => {
    ws = null;
    stopHeartbeat();
    // Without this, a request that was sent but hadn't gotten a response
    // yet when the connection dropped would just hang forever — the
    // promise never resolves or rejects, so the UI stays stuck on
    // "submitting" indefinitely instead of surfacing an error the person
    // can actually act on (retry, or wait for reconnection).
    for (const [id, { reject }] of pending) {
      reject(new Error("Connection lost — please try again."));
    }
    pending.clear();
    reconnectTimer = setTimeout(connect, 3000);
  };
}
connect();

// Sends an RPC request and resolves with `data` on success, or rejects with
// an Error (with a `.status` matching the server's HTTP-style status code)
// on failure. Waits briefly for the socket to be open/reconnected rather
// than failing immediately if it's mid-reconnect.
export function wsRequest(action, payload, token) {
  return new Promise((resolve, reject) => {
    const id = String(nextId++);
    pending.set(id, { resolve, reject, hadToken: !!token });

    const send = () => {
      try {
        ws.send(JSON.stringify({ id, action, payload: payload || {}, token, demoRouteToken }));
      } catch {
        pending.delete(id);
        reject(new Error("Could not reach backend."));
      }
    };

    if (ws && ws.readyState === WebSocket.OPEN) {
      send();
      return;
    }
    const waitStart = Date.now();
    const waitForOpen = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        clearInterval(waitForOpen);
        send();
      } else if (Date.now() - waitStart > 10000) {
        clearInterval(waitForOpen);
        pending.delete(id);
        reject(new Error("Could not reach backend."));
      }
    }, 100);
  });
}

// Subscribe to one-way broadcast messages ({type: "..."} shaped, no `id`).
// Returns an unsubscribe function.
export function onBroadcast(fn) {
  broadcastListeners.add(fn);
  return () => broadcastListeners.delete(fn);
}

// Fires whenever any wsRequest call rejects with a 428 — the current
// account needs to set up mandatory 2FA before it can do anything
// else admin-gated. See enforceMandatoryTotp on the backend.
export function onMandatoryTotpRequired(fn) {
  mandatoryTotpListeners.add(fn);
  return () => mandatoryTotpListeners.delete(fn);
}

// Fires whenever a request that carried a token gets a genuine 401 back
// — the session it was relying on is no longer valid (expired, or
// whatever it pointed at is gone, e.g. a stale tab left open across a
// fresh install). Never fires for a token-less 401 like a failed
// login attempt — see the hadToken check where this is invoked, above.
export function onSessionInvalid(fn) {
  sessionInvalidListeners.add(fn);
  return () => sessionInvalidListeners.delete(fn);
}

// For the handful of direct HTTP fetch call sites in AdminApp.jsx
// (file uploads) that don't go through wsRequest above at all, but
// still need to surface a 428 the same way — called manually once
// they see one on their own response.
export function triggerMandatoryTotpRequired() {
  mandatoryTotpListeners.forEach((fn) => fn());
}

// Same manual-trigger pattern as triggerMandatoryTotpRequired above,
// for the same handful of direct-fetch call sites — those always send
// a real token (file uploads only happen once logged in), so a 401
// from one of them is unconditionally a genuine invalidated session,
// no hadToken check needed the way the automatic wsRequest path above
// requires one.
export function triggerSessionInvalid() {
  sessionInvalidListeners.forEach((fn) => fn());
}
